/**
 * Rating calculation utilities
 */

import type { RatingDistribution } from '@hummii/shared-types';

/**
 * Source of rating data as returned by the profile endpoints.
 */
export interface RatingSource {
  rating?: number | null;
  reviewsCount?: number | null;
  ratingDistribution?: RatingDistribution | null;
}

/**
 * Calculate total number of reviews from rating distribution
 */
export function calculateTotalReviews(distribution: RatingDistribution | null | undefined): number {
  if (!distribution) return 0;

  return (
    distribution.fiveStars +
    distribution.fourStars +
    distribution.threeStars +
    distribution.twoStars +
    distribution.oneStar
  );
}

/**
 * Get rating stats for display.
 *
 * The average always comes from the API: `ratingDistribution` holds whole-star
 * buckets (every review is rounded before counting), so a single 4.5 review
 * lands in `fiveStars` and an average recomputed from the buckets reads 5.0.
 * The review count is the same either way, so the buckets stay a valid
 * fallback for it.
 */
export function getRatingStats(source: RatingSource | null | undefined): {
  rating: number;
  reviewCount: number;
} {
  if (!source) {
    return { rating: 0, reviewCount: 0 };
  }

  return {
    rating: Number(source.rating ?? 0),
    reviewCount: source.reviewsCount ?? calculateTotalReviews(source.ratingDistribution),
  };
}
